import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Space } from 'antd';
import FilterTeamsModal from './components/FilterTeamsModal';
import Header from './components/Header';
import NewTeamFormDrawer from './components/NewTeamFormDrawer';
import TeamsTable from './components/TeamsTable';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { getTableChangeParams } from 'utils/tableHelpers';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import Button from 'components/new-ui/base/button/Button';
import useTeamsFilters, { TeamsSortParams } from '../useTeamsFilters';
import useTeams from 'hooks/useTeams';
import useSession from 'hooks/useSession';
import { StyledTag } from 'components/new-ui/base/tag/styles';
import { Team } from 'store/teams/types';
import AssignTeamManagerModal from '../components/AssignTeamManagerModal';
import { IndividualType } from 'store/individuals/individual';

const TeamsPage = () => {
    const { isFreightForwarderAdminUser, isSuperAdminUser, isSystemAdminUser, customerId } = useSession();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const navigate = useNavigate();
    const {
        queryInputVal,
        appliedParams,
        antdPagination,
        market,
        status,
        tags,
        sortState,
        setMarket,
        setSize,
        setSortState,
        clearFilter,
        clearAll,
        setPage,
        setQueryInputVal,
        setStatus,
    } = useTeamsFilters();
    const { isLoading: loading, teamsResult, listTeams } = useTeams();

    const [showCreateTeamDrawer, setShowCreateTeamDrawer] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showAssignTeamManagerModal, setShowAssignTeamManagerModal] = useState(false);
    const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
    const [inRowClicked, setInRowClicked] = useState(false);

    const canUpdate = isSuperAdminUser || isSystemAdminUser || isFreightForwarderAdminUser;

    const handleAssignTeamManagerSuccess = () => {
        setShowAssignTeamManagerModal(false);
        listTeams(appliedParams);

        // If we've clicked on the row, navigate into the team
        if (inRowClicked) {
            setInRowClicked(false);
            navigate(`/teams/${selectedTeamId}`);
        }
    };

    useEffect(() => {
        listTeams(appliedParams);
    }, [listTeams, appliedParams]);

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Home',
                path: '/home',
            },
            {
                breadcrumbName: 'Teams',
                path: '',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    return (
        <div>
            <Header canUpdate={canUpdate} setShowCreateTeamDrawer={setShowCreateTeamDrawer} />
            <Row style={{ marginTop: 24 }}>
                <Space size={8}>
                    <SearchBar
                        style={{ minWidth: 360 }}
                        inputPlaceholder="Search teams"
                        value={queryInputVal}
                        onSearch={(val) => {
                            setQueryInputVal(val);
                            setPage(0);
                        }}
                        onClear={() => {
                            setQueryInputVal('');
                            setPage(0);
                        }}
                    />
                    <Button onClick={() => setShowFilterModal(true)}>Filter</Button>
                </Space>
            </Row>
            <Row style={{ marginTop: 16 }}>
                {tags?.map((t) => (
                    <StyledTag
                        closable
                        onClose={(e) => {
                            e.preventDefault();
                            clearFilter(t.key);
                        }}
                    >
                        {t.value}
                    </StyledTag>
                ))}
                {tags?.length > 0 && (
                    <Button type="link" onClick={clearAll}>
                        Clear All Filters
                    </Button>
                )}
            </Row>
            {teamsResult?.list && (
                <TeamsTable
                    canAssign={canUpdate}
                    loading={loading}
                    teams={teamsResult}
                    onAssignTeam={(record: Team) => {
                        setSelectedTeamId(record.id);
                        setShowAssignTeamManagerModal(true);
                    }}
                    onRowClick={(record: Team) => {
                        setSelectedTeamId(record.id);

                        const teamManagers = record.members.filter(
                            (member) => member.role === IndividualType.TEAM_MANAGER
                        );

                        if (teamManagers.length > 0) {
                            navigate(`/teams/${record.id}`);
                        } else if (!teamManagers.length && canUpdate) {
                            // If we don't have a team manager assigned, we show the modal
                            setInRowClicked(true);
                            setShowAssignTeamManagerModal(true);
                        }
                    }}
                    pagination={{
                        ...antdPagination,
                        total: teamsResult.total,
                        position: ['bottomCenter'],
                        hideOnSinglePage: true,
                    }}
                    onChange={(antPagination, _filters, sorter) => {
                        const params = getTableChangeParams({ pagination: antPagination, sorter });
                        const { page, size, sortParameter, sortDirection } = params;

                        setPage(typeof page === 'number' ? page : antdPagination.current);
                        setSize(typeof size === 'number' ? size : antdPagination.pageSize);

                        if (sortParameter && sortDirection) {
                            setSortState({
                                teamSortParameter: sortParameter as TeamsSortParams,
                                sortDirection,
                            });
                        }
                    }}
                    currentSortDirection={sortState.sortDirection}
                    currentSortParam={sortState.teamSortParameter}
                />
            )}
            {showCreateTeamDrawer && (
                <NewTeamFormDrawer
                    onSuccess={() => {
                        listTeams(appliedParams);
                        setShowCreateTeamDrawer(false);
                    }}
                    onClose={() => setShowCreateTeamDrawer(false)}
                    open={showCreateTeamDrawer}
                />
            )}
            {showAssignTeamManagerModal && canUpdate && (
                <AssignTeamManagerModal
                    customerId={customerId}
                    teamId={selectedTeamId}
                    onCancel={() => setShowAssignTeamManagerModal(false)}
                    onSuccess={handleAssignTeamManagerSuccess}
                />
            )}
            <FilterTeamsModal
                open={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                queryInputVal={queryInputVal}
                setQueryInputVal={setQueryInputVal}
                setPage={setPage}
                market={market}
                setMarket={setMarket}
                clearAll={clearAll}
                status={status}
                setStatus={setStatus}
            />
        </div>
    );
};

export default TeamsPage;
